import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Table, Input, Space, Typography, Button, Tag, Select, DatePicker, Descriptions } from 'antd';
import { ReloadOutlined, UserOutlined, AimOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const getToken = () => {
    const value = `; ${document.cookie}`;
    const parts = value.split('; token=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

const methodColor = {
    GET: 'blue', POST: 'green', PUT: 'orange', DELETE: 'red', PATCH: 'purple',
};

const roleColor = {
    admin: 'gold', superadmin: 'volcano', support: 'cyan', viewer: 'default',
};

const targetTypeColor = {
    user: 'geekblue', subscription: 'purple', product: 'magenta', license: 'cyan',
};

const ActivityLog = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState(null);
    const [range, setRange] = useState(null);
    const navigate = useNavigate();

    const fetchData = useCallback(async (overrides = {}) => {
        setLoading(true);
        try {
            const params = {};
            const s = overrides.search !== undefined ? overrides.search : search;
            const a = overrides.action !== undefined ? overrides.action : actionFilter;
            const r = overrides.range !== undefined ? overrides.range : range;
            if (s) params.search = s;
            if (a) params.action = a;
            if (r && r[0] && r[1]) {
                params.from = r[0].startOf('day').toISOString();
                params.to = r[1].endOf('day').toISOString();
            }
            const res = await api.get('/admin-activity', {
                params,
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setData(res.data);
        } catch (error) {
            if (error.response?.status === 401) {
                alert('Session expired. Redirecting to login.');
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, search, actionFilter, range]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Distinct action labels for the filter dropdown, derived from loaded rows.
    const actionOptions = useMemo(() => {
        const set = new Set(data.map((r) => r.action).filter(Boolean));
        return Array.from(set).sort().map((a) => ({ label: a, value: a }));
    }, [data]);

    const columns = [
        {
            title: 'Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (d) => (d ? new Date(d).toLocaleString() : '—'),
            width: 185,
        },
        {
            title: 'Who (Admin)',
            key: 'actor',
            render: (_, r) => (
                <div>
                    <Space size={4}>
                        <UserOutlined style={{ color: '#888' }} />
                        <span>{r.actorUsername || '—'}</span>
                        {r.actorRole && <Tag color={roleColor[r.actorRole] || 'default'}>{r.actorRole}</Tag>}
                    </Space>
                    <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.actorEmail || ''}</Typography.Text>
                    </div>
                </div>
            ),
        },
        { title: 'Did What', dataIndex: 'action', key: 'action', width: 190 },
        {
            title: 'For Whom (Target)',
            key: 'target',
            width: 240,
            render: (_, r) => {
                if (!r.targetLabel && !r.targetId) {
                    return <Typography.Text type="secondary">—</Typography.Text>;
                }
                const primary = r.targetLabel || r.targetId;
                return (
                    <Space size={4} direction="vertical" style={{ rowGap: 0, width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: 224 }}>
                            <AimOutlined style={{ color: '#888', flexShrink: 0 }} />
                            <Typography.Text
                                style={{ flex: 1, minWidth: 0 }}
                                ellipsis={{ tooltip: primary }}
                            >
                                {primary}
                            </Typography.Text>
                            {r.targetType && (
                                <Tag color={targetTypeColor[r.targetType] || 'default'} style={{ flexShrink: 0, marginInlineEnd: 0 }}>
                                    {r.targetType}
                                </Tag>
                            )}
                        </div>
                        {r.targetLabel && r.targetId && r.targetLabel !== r.targetId && (
                            <Typography.Text
                                type="secondary"
                                style={{ fontSize: 11, maxWidth: 224 }}
                                ellipsis={{ tooltip: r.targetId }}
                                copyable={{ text: r.targetId }}
                            >
                                {r.targetId}
                            </Typography.Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Endpoint',
            key: 'endpoint',
            render: (_, r) => (
                <Space>
                    <Tag color={methodColor[r.method] || 'default'}>{r.method}</Tag>
                    <Typography.Text code style={{ fontSize: 12 }}>{r.path}</Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 90,
            render: (s) => (
                <Tag color={s >= 200 && s < 300 ? 'green' : s >= 400 ? 'red' : 'default'}>{s ?? '—'}</Tag>
            ),
        },
    ];

    // Detailed activity, shown when a row is expanded.
    const expandedRowRender = (r) => {
        const hasPayload = r.requestSummary && Object.keys(r.requestSummary).length > 0;
        return (
            <Descriptions
                size="small"
                column={2}
                bordered
                style={{ background: '#fafafa', padding: 8, borderRadius: 6 }}
            >
                <Descriptions.Item label="Actor email">{r.actorEmail || '—'}</Descriptions.Item>
                <Descriptions.Item label="Actor role">{r.actorRole || '—'}</Descriptions.Item>
                <Descriptions.Item label="Target type">{r.targetType || '—'}</Descriptions.Item>
                <Descriptions.Item label="Target id">{r.targetId || '—'}</Descriptions.Item>
                <Descriptions.Item label="IP address">{r.ip || '—'}</Descriptions.Item>
                <Descriptions.Item label="Status code">{r.statusCode ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="User agent" span={2}>{r.userAgent || '—'}</Descriptions.Item>
                <Descriptions.Item label="Request payload" span={2}>
                    {hasPayload ? (
                        <pre style={{ margin: 0, maxHeight: 240, overflow: 'auto', fontSize: 12 }}>
                            {JSON.stringify(r.requestSummary, null, 2)}
                        </pre>
                    ) : (
                        <Typography.Text type="secondary">No request body</Typography.Text>
                    )}
                </Descriptions.Item>
            </Descriptions>
        );
    };

    const resetFilters = () => {
        setSearch('');
        setActionFilter(null);
        setRange(null);
        fetchData({ search: '', action: null, range: null });
    };

    return (
        <div style={{ padding: '20px' }}>
            <Title level={2}>Admin Activity</Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
                Detailed audit trail — who did what, for whom. Click a row to expand full details.
            </Typography.Paragraph>
            <Space style={{ marginBottom: 16 }}>
                <Button onClick={() => navigate('/dashboard')}>Dashboard</Button>
                <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>Refresh</Button>
            </Space>
            <br />
            <Space wrap style={{ marginBottom: '20px' }}>
                <Search
                    placeholder="Search admin, action, endpoint or target"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onSearch={(v) => fetchData({ search: v })}
                    enterButton
                    allowClear
                    style={{ width: 360 }}
                />
                <Select
                    placeholder="Filter by action"
                    allowClear
                    style={{ width: 220 }}
                    value={actionFilter}
                    options={actionOptions}
                    onChange={(v) => { setActionFilter(v ?? null); fetchData({ action: v ?? null }); }}
                />
                <RangePicker
                    value={range}
                    onChange={(v) => { setRange(v); fetchData({ range: v }); }}
                />
                <Button onClick={resetFilters}>Reset</Button>
            </Space>
            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey="_id"
                expandable={{ expandedRowRender }}
                pagination={{ pageSize: 20, showSizeChanger: true }}
            />
        </div>
    );
};

export default ActivityLog;
